/**
 * Column definitions for restaurant tables (full table and map side table).
 */
import { createColumnHelper } from "@tanstack/react-table";

import {
  DataTableColumnHeader,
  DateCell,
  EmptyValue,
  PriceCell,
  RatingCell,
  RowActions,
} from "@/components/data-table";
import { getPrimaryCuisine } from "@/lib/cuisine";
import { nearestCityName } from "@/constants/cities";

import { Restaurant } from "../types/restaurants.types";
import { RestaurantRowActions } from "./restaurant-row-actions";
import { HeartCell } from "./heart-cell";

const columnHelper = createColumnHelper<Restaurant>();

const ratingSort = (a: Restaurant, b: Restaurant) =>
  parseFloat(a.rating || "0") - parseFloat(b.rating || "0");

const nameColumn = columnHelper.accessor("name", {
  header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
  cell: (info) => <span className="font-semibold text-foreground">{info.getValue()}</span>,
});

/** Name column whose cell is a button, making it the row's keyboard-reachable primary control. */
const openableNameColumn = (onOpen: (r: Restaurant) => void) =>
  columnHelper.accessor("name", {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row, getValue }) => (
      <button
        type="button"
        className="text-left font-semibold text-foreground hover:text-brand"
        onClick={(e) => {
          e.stopPropagation();
          onOpen(row.original);
        }}
      >
        {getValue()}
      </button>
    ),
  });

const cuisineColumn = columnHelper.accessor((row) => getPrimaryCuisine(row.types) ?? "", {
  id: "cuisine",
  header: ({ column }) => <DataTableColumnHeader column={column} title="Cuisine" />,
  cell: (info) => (info.getValue() ? <span className="text-muted-foreground">{info.getValue()}</span> : <EmptyValue />),
});

const cityColumn = columnHelper.accessor((row) => nearestCityName(row.latitude, row.longitude) ?? "", {
  id: "city",
  header: ({ column }) => <DataTableColumnHeader column={column} title="City" />,
  cell: (info) => (info.getValue() ? <span className="text-muted-foreground">{info.getValue()}</span> : <EmptyValue />),
});

const ratingColumn = columnHelper.accessor("rating", {
  header: ({ column }) => <DataTableColumnHeader column={column} title="Rating" align="right" />,
  cell: (info) => <RatingCell rating={info.getValue()} />,
  sortingFn: (a, b) => ratingSort(a.original, b.original),
  meta: { align: "right" },
});

const heartsColumn = columnHelper.accessor((row) => row.hearts?.count ?? 0, {
  id: "hearts",
  header: ({ column }) => <DataTableColumnHeader column={column} title="Hearts" />,
  cell: ({ row }) => <HeartCell restaurant={row.original} />,
});

const priceColumn = columnHelper.accessor("priceLevel", {
  header: ({ column }) => <DataTableColumnHeader column={column} title="Price" />,
  cell: (info) => <PriceCell priceLevel={info.getValue()} />,
});

const listsColumn = columnHelper.accessor("lists", {
  header: "Lists",
  enableSorting: false,
  cell: (info) => {
    const lists = info.getValue();
    if (!lists || lists.length === 0) return <span className="text-muted-foreground">Not in a list</span>;
    const [first, ...rest] = lists;
    return (
      <span className="text-muted-foreground">
        <span className="font-medium text-foreground">{first?.listName}</span>
        {rest.length > 0 && `, ${rest.map((l) => l.listName).join(", ")}`}
      </span>
    );
  },
});

const savedColumn = columnHelper.accessor("createdAt", {
  header: ({ column }) => <DataTableColumnHeader column={column} title="Saved" />,
  cell: (info) => <DateCell date={info.getValue()} />,
  sortingFn: "datetime",
});

const actionsColumn = (listId?: string) =>
  columnHelper.display({
    id: "actions",
    header: "",
    cell: ({ row }) => (
      <RowActions>
        <RestaurantRowActions restaurant={row.original} listId={listId} />
      </RowActions>
    ),
  });

export function getRestaurantColumns(
  options: { listId?: string; showLists?: boolean; onOpen?: (r: Restaurant) => void } = {}
) {
  const { listId, showLists = true, onOpen } = options;
  return [
    onOpen ? openableNameColumn(onOpen) : nameColumn,
    cuisineColumn,
    cityColumn,
    ratingColumn,
    priceColumn,
    heartsColumn,
    ...(showLists ? [listsColumn] : []),
    savedColumn,
    actionsColumn(listId),
  ];
}

/** Four-column variant for the table beside the map. */
export const restaurantMapColumns = [nameColumn, cuisineColumn, ratingColumn, priceColumn, heartsColumn];
